// @ts-nocheck
/*
 * Port of views/dashboard/today-running-campaigns.js.
 * API: API_DASHBOARD_TODAY_RUNNING_CAMPAIGN_SUMMARY_STATS.
 * Columns mirror the Live Campaigns list (CampaignsListPage) data columns.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_DASHBOARD_TODAY_RUNNING_CAMPAIGN_SUMMARY_STATS } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/data-table/DataTable';

const TEMPLATE_MAP = { '0': 'Simple IVR', '1': 'DTMF', '2': 'Call Patch', '3': 'Custom IVR' };
const DIALER_MAP = { '1': 'Click2Call', '2': 'Auto Dialer', '3': 'Predictive Dialer', '4': 'Power Dialer' };

const formatDateTime = (date, time) => {
  if (!date || !time) return '—';
  const d = new Date(`${date}T${time}`);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
};

const formatCreated = (v) => {
  const d = new Date(v);
  if (!v || isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const kv = (label, value) => (
  <div className="whitespace-nowrap text-xs tabular-nums">
    <span className="text-muted-foreground">{label}: </span>
    <span className="text-foreground">{value}</span>
  </div>
);

export default function TodayRunningCampaigns() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const seqRef = useRef(0);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        if (user.role) setLoggedInUserRole(user.role);
      } catch (e) {}
    }
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole == USER_ROLES.CALL_CENTRE_AGENT) return;
    fetchData();

  }, [loggedInUserRole]);

  const fetchData = (isRefresh = false) => {
    const seq = ++seqRef.current;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    getApiCall(API_DASHBOARD_TODAY_RUNNING_CAMPAIGN_SUMMARY_STATS)
      .then((resp) => {
        if (seq !== seqRef.current) return;
        const formatted = (resp.data || resp).map((item) => ({
          ...item,
          campaignId: item.campaignId?.toString(),
          campaignName: item.campaignName?.toString(),
        }));
        setData(formatted);
      })
      .catch((err) => toast.error(friendlyApiError(err) || 'Failed to fetch data'))
      .finally(() => {
        if (seq !== seqRef.current) return;
        setLoading(false);
        setRefreshing(false);
      });
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: 'campaignName',
        // ID + username kept in the accessor so search still matches them.
        accessorFn: (r) => `${r.campaignName} ${r.campaignId} ${r.username ?? ''}`,
        header: 'Outbound Campaign',
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              className="text-left font-medium text-primary underline-offset-2 hover:underline dark:!text-slate-100"
              onClick={() => navigate(`/${ROUTES.CAMPAIGNS.DETAIL.replace(':id', row.original.campaignId)}`)}
            >
              {row.original.campaignName}
            </button>
            {kv('ID', row.original.campaignId)}
            {kv('User', row.original.username || '—')}
          </div>
        ),
      },
      {
        id: 'schedule',
        accessorFn: (r) => `${r.startDate ?? ''} ${r.startTime ?? ''}`,
        header: 'Campaign Date',
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            {kv('Start Date', formatDateTime(row.original.startDate, row.original.startTime))}
            {kv('End Date', formatDateTime(row.original.endDate, row.original.endTime))}
            {kv('Created At', formatCreated(row.original.createdAt))}
          </div>
        ),
      },
      {
        accessorKey: 'campaignStatus',
        header: 'Status',
        // ponytail: endpoint returns only status 1 (Running); full status map lives in CampaignsListPage.
        cell: ({ row }) =>
          Number(row.original.campaignStatus) === 1 ? (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900">
              <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-emerald-500" />
              Running
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'setup',
        accessorFn: (r) => `${TEMPLATE_MAP[r.templateId] || 'N/A'} / ${DIALER_MAP[r.campaignType] || 'N/A'}`,
        header: 'Setup',
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            {kv('Template', TEMPLATE_MAP[row.original.templateId] || 'N/A')}
            {kv('Dialer', DIALER_MAP[row.original.campaignType] || 'N/A')}
          </div>
        ),
      },
      {
        accessorKey: 'totalNumbers',
        header: 'Total Numbers',
        cell: ({ row }) => <span className="font-semibold tabular-nums text-foreground">{row.original.totalNumbers || 0}</span>,
      },
      {
        accessorKey: 'callsDialed',
        header: 'Completed',
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">{row.original.callsDialed || 0}</span>
        ),
      },
      {
        id: 'remaining',
        accessorFn: (r) => Math.max(0, Number(r.totalNumbers || 0) - Number(r.callsDialed || 0)),
        header: 'Remaining',
        cell: ({ getValue }) => (
          <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">{getValue()}</span>
        ),
      },
      {
        accessorKey: 'totalCallsInitiated',
        header: 'Total Calls Initiated',
        cell: ({ row }) => <span className="tabular-nums">{Number(row.original.totalCallsInitiated) || 0}</span>,
      },
      { accessorKey: 'connectedCalls', header: 'Answered Calls', cell: ({ row }) => row.original.connectedCalls ?? 0 },
      { accessorKey: 'dndCount', header: 'DnD Count', cell: ({ row }) => row.original.dndCount ?? 0 },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-foreground">Today's Running Campaigns</span>
          {data.length > 0 && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary dark:bg-white/10 dark:!text-slate-100">
              {data.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => fetchData(true)}
          disabled={loading || refreshing}
          title="Refresh running campaigns"
          aria-label="Refresh running campaigns"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw className={'h-3.5 w-3.5 ' + (refreshing ? 'animate-spin' : '')} />
          Refresh
        </button>
      </div>

      <DataTable
        tableId="dashboard.today-running-campaigns"
        columns={columns}
        data={data}
        isLoading={loading}
        searchPlaceholder="Search by campaign name or ID…"
        emptyTitle="No running campaigns"
        emptyDescription="No campaign is running right now."
        pageSize={10}
        exportFilename="today-running-campaigns.csv"
      />
    </>
  );
}
